import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SourceIcon from '@mui/icons-material/Article';
import custodiaService from '../services/custodiaService';

const ChatInterface = ({ conversationId, initialMessages = [] }) => {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages(initialMessages);
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading || !conversationId) return;

    const userMessage = { role: 'user', content: trimmed, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await custodiaService.sendMessage(conversationId, trimmed);
      const data = res.data?.data || {};
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.answer || 'No se pudo obtener respuesta.',
        sources: data.sources || [],
        createdAt: new Date().toISOString(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error al procesar la consulta. Intente de nuevo.',
        sources: [],
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
        {messages.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, opacity: 0.7 }}>
            <SmartToyIcon sx={{ fontSize: 64, color: 'grey.300' }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              Haga una pregunta
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
              La IA buscará en los documentos seleccionados y le dará respuestas con referencias a las fuentes.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
              {['Resumen general del contenido', 'Fechas y plazos importantes', 'Partes involucradas'].map((s, i) => (
                <Chip key={i} label={s} variant="outlined" size="small" onClick={() => setInput(s)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' } }} />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.200',
                  color: msg.role === 'user' ? 'white' : 'text.primary', flexShrink: 0,
                }}>
                  {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                </Box>
                <Box sx={{ maxWidth: '75%' }}>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'white' : 'text.primary',
                    border: msg.role === 'assistant' ? 1 : 0, borderColor: 'divider',
                  }}>
                    <Typography variant="body2"
                      dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                    />
                  </Paper>
                  {msg.sources?.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {msg.sources.map((src, i) => (
                        <Tooltip key={i} title={`Pág. ~${src.pageEstimate || '?'} | Score: ${src.score || ''}`}>
                          <Chip icon={<SourceIcon sx={{ fontSize: '14px !important' }} />}
                            label={src.fileName || `Fuente ${i + 1}`}
                            size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 24 }} />
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" color={msg.role === 'user' ? 'primary.100' : 'text.disabled'}
                    sx={{ mt: 0.5, display: 'block' }}>
                    {new Date(msg.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200', flexShrink: 0 }}>
                  <SmartToyIcon fontSize="small" />
                </Box>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Buscando en sus documentos...</Typography>
                  </Box>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', p: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField fullWidth multiline maxRows={3} size="small"
          placeholder="Escriba su pregunta..."
          value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress}
          disabled={loading || !conversationId}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton onClick={handleSend} disabled={!input.trim() || loading || !conversationId}
          sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' }, width: 40, height: 40 }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatInterface;
